import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { authApi, subscriptionApi } from '@/services/api'
import { setToken } from '@/lib/utils'
import { toast } from 'sonner'
import { Mail, Loader2, CheckCircle, XCircle } from 'lucide-react'
import { motion } from 'framer-motion'

export function VerifyEmailPage() {
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [autoVerifying, setAutoVerifying] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  const userAlias = localStorage.getItem('user') || searchParams.get('alias')
  const codeFromUrl = searchParams.get('code')

  useEffect(() => {
    if (!userAlias) {
      navigate('/login')
      return
    }

    // Auto-verify if code is in URL
    if (codeFromUrl) {
      setCode(codeFromUrl)
      handleVerify(codeFromUrl)
    }
  }, [userAlias, codeFromUrl, navigate])

  const handleVerify = async (verificationCode = code) => {
    if (!verificationCode) {
      toast.error('Please enter the verification code')
      return
    }

    const isAutoVerify = !!codeFromUrl
    if (isAutoVerify) {
      setAutoVerifying(true)
    } else {
      setLoading(true)
    }
    
    setError('')

    try {
      const response = await authApi.verifyEmail({
        user: userAlias,
        token: verificationCode
      })

      if (response.data.access) {
        // Set token first so API calls will use it
        setToken(response.data.access)
        
        // Check subscription status after verification
        let subscriptionData = null
        try {
          const subResponse = await subscriptionApi.getCurrentSubscription()
          subscriptionData = subResponse.data
        } catch (subError) {
          console.log('Subscription check failed:', subError)
        }

        // Remove user alias
        localStorage.removeItem('user')
        
        toast.success('Email verified successfully!')
        
        // Check redirect logic
        const redirectPath = localStorage.getItem('ldirect')
        if (redirectPath && subscriptionData && subscriptionData.has_access) {
          localStorage.removeItem('ldirect')
          navigate(redirectPath)
        } else {
          // Check for ghost-guest flow
          const ghostGuest = localStorage.getItem('ghost-guest')
          if (ghostGuest) {
            const b64Email = btoa(ghostGuest)
            localStorage.removeItem('ghost-guest')
            navigate(`/ghost/?guest=${b64Email}`)
          } else {
            navigate('/pricing')
          }
        }
      }
    } catch (error) {
      console.error('Verification error:', error)
      const errorMsg = error.response?.data?.msg || error.response?.data?.message || 'Invalid verification code'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setLoading(false)
      setAutoVerifying(false)
    }
  }

  // Check activation status periodically
  const checkUserActivation = async () => {
    if (!userAlias) return
    
    try {
      const response = await authApi.checkActivation(userAlias)
      
      if (response.data.verified) {
        const redirectPath = localStorage.getItem('ldirect')
        navigate(redirectPath || '/')
      }
    } catch (error) {
      console.log('User not activated yet')
    }
  }

  // Poll for activation every 30 seconds
  useEffect(() => {
    if (!userAlias) return

    const intervalId = setInterval(() => {
      checkUserActivation()
    }, 30000) // 30 seconds

    return () => clearInterval(intervalId)
  }, [userAlias])

  const handleResend = async () => {
    setResending(true)
    try {
      await authApi.resendVerification(userAlias)
      toast.success('Verification code sent! Check your email.')
    } catch (error) {
      toast.error('Failed to resend code. Please try again.')
    } finally {
      setResending(false)
    }
  }

  if (autoVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Verifying your email...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8"
      >
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center">
            <Mail className="w-10 h-10 text-primary" />
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Verify Your Email</h1>
          <p className="text-gray-600">
            We've sent a verification code to your email address. Please enter it below to continue.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={(e) => { e.preventDefault(); handleVerify(); }} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verification Code
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-widest font-mono"
              placeholder="XXXXXX"
              maxLength={6}
              disabled={loading}
              autoFocus
            />
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            size="lg"
            disabled={loading || !code}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Verify Email
              </>
            )}
          </Button>
        </form>

        {/* Resend */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 mb-2">
            Didn't receive the code?
          </p>
          <Button
            variant="ghost"
            onClick={handleResend}
            disabled={resending}
            className="text-primary hover:text-primary"
          >
            {resending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              'Resend Code'
            )}
          </Button>
        </div>

        {/* Back to login */}
        <div className="mt-8 text-center">
          <button
            onClick={() => {
              localStorage.removeItem('user')
              navigate('/login')
            }}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Back to Login
          </button>
        </div>
      </motion.div>
    </div>
  )
}
